import React, { useMemo } from 'react';
import { TableCell, Box } from '@mui/material';
import type { TableColumnDescriptor, Locale, ReferenceDisplayValue } from '@finance/contracts';
import { resolveCellRenderer } from './renderers/cell-renderer-registry.ts';
import type { EvaluationContext } from '../../../runtime/conditions/evaluate-condition.ts';
import { resolveActions, type ResolvedAction } from '../../../runtime/actions/action-resolution';
import { tableTokens } from './table-tokens.ts';
import type { TableActionContext } from '../TableRenderer.tsx';
import { resolveColumnSizing } from './column-sizing.js';
import { ActionRenderer } from '../ActionRenderer.tsx';

interface TableCellProps {
  column: TableColumnDescriptor;
  row: Record<string, unknown>;
  evalCtx: EvaluationContext;
  locale: Locale;
  onAction?: (action: ResolvedAction, context: TableActionContext) => void | Promise<void>;
  referenceDisplayMap?: Record<string, Record<string, ReferenceDisplayValue>> | null;
  pinned?: 'left' | 'right' | 'center';
  leftOffset?: number;
  rightOffset?: number;
  width?: number;
}

export function TableCellRenderer({
  column,
  row,
  evalCtx,
  locale,
  onAction,
  referenceDisplayMap,
  pinned = 'center',
  leftOffset = 0,
  rightOffset = 0,
  width,
}: TableCellProps) {
  const value = row[column.field];
  const cellWidth = width ?? column.defaultWidth;
  const sizing = resolveColumnSizing(column, cellWidth);
  const wraps = column.wrapping === 'wrap';
  const truncates = column.truncation !== 'none';

  const rendererDescriptor = useMemo(() => resolveCellRenderer(column), [column]);
  const RendererComponent = rendererDescriptor.component;

  const cellActions = useMemo(() => {
    if (!column.actions) return [];
    return resolveActions(column.actions, {
      ctx: evalCtx,
      data: row,
    });
  }, [column.actions, evalCtx, row]);

  // P0-4: Permission checks are server-side only. The descriptor sent by the server
  // already reflects access control; trust it rather than doing client-side permission verification.
  // If a column is in the descriptor, the server has already authorized its visibility.

  const cellSx: React.CSSProperties = {
    textAlign: column.align ?? 'left',
    whiteSpace: wraps ? 'normal' : 'nowrap',
    // Keep content inside its column even when the descriptor requests no
    // ellipsis. `truncation: none` means clip rather than paint across the next
    // cell; wrapping remains controlled independently by `wrapping`.
    overflow: 'hidden',
    textOverflow: truncates ? 'ellipsis' : 'clip',
    overflowWrap: wraps ? 'anywhere' : undefined,
    wordBreak: wraps ? 'break-word' : undefined,
    position: pinned !== 'center' ? 'sticky' : undefined,
    left: pinned === 'left' ? `${leftOffset}px` : undefined,
    right: pinned === 'right' ? `${rightOffset}px` : undefined,
    zIndex: pinned !== 'center' ? tableTokens.zIndex.pinnedBody : undefined,
    backgroundColor: pinned !== 'center' ? 'inherit' : undefined,
    ...(pinned === 'left' && { boxShadow: tableTokens.pinned.shadowRight }),
    ...(pinned === 'right' && { boxShadow: tableTokens.pinned.shadowLeft }),
  };

  if (sizing.width !== undefined) cellSx.width = sizing.width;
  cellSx.minWidth = sizing.minWidth;
  cellSx.maxWidth = sizing.maxWidth;

  return (
    <TableCell sx={cellSx}>
      <Box
        sx={{
          display: 'flex',
          alignItems: wraps ? 'flex-start' : 'center',
          minWidth: 0,
          maxWidth: '100%',
          gap: 0.5,
          width: '100%',
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: truncates ? 'ellipsis' : 'clip',
            whiteSpace: wraps ? 'normal' : 'nowrap',
            overflowWrap: wraps ? 'anywhere' : undefined,
            wordBreak: wraps ? 'break-word' : undefined,
            flexShrink: 1,
            flexGrow: 1,
          }}
        >
          <RendererComponent
            value={value}
            column={column}
            row={row}
            locale={locale}
            referenceDisplayMap={referenceDisplayMap}
          />
        </Box>
        {cellActions.length > 0 && (
          <Box
            sx={{ flexShrink: 0, display: 'flex', gap: 0.5, ml: 0.5 }}
            onClick={event => event.stopPropagation()}
            data-table-interactive="true"
          >
            {cellActions.map(resolved => (
              <Box key={resolved.action.id} data-table-interactive="true">
                <ActionRenderer
                  action={resolved}
                  size="small"
                  context={{ source: 'table-row', rowData: row }}
                  onAction={(next, executionContext) =>
                    onAction?.(next, {
                      source: 'cell-action',
                      row,
                      confirmationHandled: executionContext?.confirmationHandled,
                      confirmationValues: executionContext?.confirmationValues,
                    })
                  }
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </TableCell>
  );
}
